import PretensionController from '@/controllers/Pretension';
import { diadocAuth, diadocAuthMode } from '@/utils/config';

/**
 * Diadoc OIDC signer auth — механизм (FML-5vmy, Часть 1).
 *
 * Framework-free сервис-оркестрации: чистые функции + window.location / window.history.
 * Часть 1 поставляет только механизм; вызов перед подписанием и размещение индикатора —
 * Часть 2. Единственные швы для Части 2: ensureSignerSession, isOidcMode, startSignerLogin,
 * signerAuthIndicatorState.
 */

export interface SignerSessionStatus {
  valid: boolean;
  /** Канон контракта — epoch ms (number|null). [F1] выровнено в Части 2. */
  expiresAt: number | null;
}

/** Дискриминированный union — Часть 2 ветвится по status без догадок. */
export type EnsureSignerSessionResult =
  | { status: 'ok' }
  | { status: 'auth_failed' }
  | { status: 'redirecting' };

export type AuthReturnParam = 'ok' | 'fail' | null;

export type SignerAuthIndicatorState = 'authorized' | 'needs_auth' | 'auth_failed';

/** Чтение фьюче-флага централизовано (config.ts) — компоненты ходят сюда, не в env. */
export function isOidcMode(): boolean {
  return diadocAuthMode === 'oidc';
}

/** Пьюр-парсер return-параметра. Вход явный (дефолт window) — тестируется без window. */
export function readAuthReturnParam(search: string = window.location.search): AuthReturnParam {
  const value = new URLSearchParams(search).get('auth');
  if (value === 'ok' || value === 'fail') return value;
  return null;
}

/**
 * URL текущей страницы для возврата после OIDC-логина: origin + pathname + query без auth.
 * Origin берётся из window.location.origin (per-env), валидацию open-redirect делает
 * diadoc-api по OIDC_RETURN_ALLOWLIST — фронт его не дублирует. Вход явный (дефолт window).
 */
export function buildReturnUrl(loc: Location = window.location): string {
  const params = new URLSearchParams(loc.search);
  params.delete('auth');
  const query = params.toString();
  return `${loc.origin}${loc.pathname}${query ? `?${query}` : ''}`;
}

/** Убрать ?auth из адресной строки без записи в историю — чтобы refresh не replay'ил param-логику. */
export function clearAuthReturnParam(): void {
  window.history.replaceState({}, '', buildReturnUrl());
}

/**
 * Единственный редирект-сайд-эффект. Браузер↔diadoc-api напрямую (OIDC-навигация, не AJAX).
 * Зовётся веткой «свежий invalid» в ensureSignerSession либо пользователем по кнопке
 * «Войти повторно» (через retry-эмит индикатора, Часть 2).
 */
export function startSignerLogin(signerEmail: string): void {
  const params = new URLSearchParams({ signer: signerEmail, return: buildReturnUrl() });
  window.location.assign(`${diadocAuth.oidcLoginUrl}?${params.toString()}`);
}

/**
 * Разворот ответа checkSignerSession в SignerSessionStatus.
 * - Talend-джобы заворачивают payload в .result (ср. single) — разворачиваем defensive-ом;
 * - valid трактуем fail-closed: всё, что не строгий true, — невалидная сессия
 *   (защита от пустого/битого ответа или HTTP-ошибки → ложного proceed).
 */
function readStatus(signerEmail: string): Promise<SignerSessionStatus> {
  return PretensionController.checkSignerSession(signerEmail).then((resp) => {
    const body = resp?.result?.[1] as any;
    const data = body?.result ?? body;
    return {
      valid: data?.valid === true,
      expiresAt: data?.expiresAt ?? null,
    };
  });
}

/**
 * Param-gated проверка сессии подписанта с анти-loop инвариантом.
 * Сначала читаем return-параметр, затем решаем (ровно три ветки):
 *   1. ?auth=fail        → auth_failed сразу, без проверки и без редиректа;
 *   2. ?auth=ok          → ровно одна проверка; valid→ok, invalid→auth_failed (анти-loop);
 *   3. параметра нет      → проверка; valid→ok, invalid→startSignerLogin + redirecting.
 * Инвариант: после потребления auth-параметра — ≤1 проверка и ноль авто-редиректов.
 */
export async function ensureSignerSession(signerEmail: string): Promise<EnsureSignerSessionResult> {
  const param = readAuthReturnParam();

  if (param === 'fail') {
    clearAuthReturnParam();
    return { status: 'auth_failed' };
  }

  if (param === 'ok') {
    clearAuthReturnParam();
    const status = await readStatus(signerEmail);
    return status.valid ? { status: 'ok' } : { status: 'auth_failed' };
  }

  const status = await readStatus(signerEmail);
  if (status.valid) return { status: 'ok' };
  startSignerLogin(signerEmail);
  return { status: 'redirecting' };
}

/** Пьюр-маппер результата в display-стейт индикатора. */
export function signerAuthIndicatorState(result: EnsureSignerSessionResult): SignerAuthIndicatorState {
  switch (result.status) {
    case 'ok': return 'authorized';
    case 'auth_failed': return 'auth_failed';
    case 'redirecting': return 'needs_auth';
    // Exhaustiveness: добавление нового члена в union даст compile-error здесь.
    default: { const exhaustive: never = result; return exhaustive; }
  }
}

/**
 * Действие гейта сессии перед подписанием (Часть 2, §6).
 * Решение, что делает страница после ensureSignerSession:
 *   - proceed          → сессия валидна, открыть UI подписания;
 *   - show_auth_failed → показать индикатор «не удалось» + кнопку повтора;
 *   - redirecting      → браузер уже уходит на OIDC-логин, страница ничего не делает.
 * Отдельно от signerAuthIndicatorState: тот мапит в display-стейт индикатора,
 * этот — в действие оркестрации страницы. Pure → тестируется без компонента.
 */
export type SigningGateAction = 'proceed' | 'show_auth_failed' | 'redirecting';

export function signingGateAction(result: EnsureSignerSessionResult): SigningGateAction {
  switch (result.status) {
    case 'ok': return 'proceed';
    case 'auth_failed': return 'show_auth_failed';
    case 'redirecting': return 'redirecting';
    // Exhaustiveness: новый член union даст compile-error здесь.
    default: { const exhaustive: never = result; return exhaustive; }
  }
}
